
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface UserProfileData {
  photoUrl: string | null;
  language: string;
  isPrivate: boolean;
  username: string | null;
}

export const useUserProfile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<UserProfileData>({
    photoUrl: null,
    language: "en",
    isPrivate: false,
    username: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Stabilize setter callbacks
  const setLanguage = useCallback((language: string) => {
    setProfileData(prev => ({ ...prev, language }));
  }, []);

  useEffect(() => {
    let isCancelled = false;
    
    // Try to get language preference from localStorage
    const storedLanguage = localStorage.getItem("preferred-language");
    if (storedLanguage && !isCancelled) {
      setProfileData(prev => ({ ...prev, language: storedLanguage }));
      document.documentElement.lang = storedLanguage;
    }
    
    const fetchUserProfile = async () => {
      if (user && !isCancelled) {
        try {
          // First check user metadata
          if (user.user_metadata?.avatar_url) {
            setProfileData(prev => ({ ...prev, photoUrl: user.user_metadata.avatar_url }));
          }
          
          // Then check profiles table
          const { data, error } = await supabase
            .from('profiles')
            .select('photo_url, profile_picture, language, is_private, username')
            .eq('id', user.id)
            .single();
            
          if (!error && data && !isCancelled) {
            const updates: Partial<UserProfileData> = {};
            
            // Use profile_picture first, then photo_url, then avatar_url
            const profilePicture = data.profile_picture || data.photo_url || user.user_metadata?.avatar_url;
            if (profilePicture) {
              updates.photoUrl = profilePicture;
            }
            
            if (data.language) {
              updates.language = data.language;
              localStorage.setItem("preferred-language", data.language);
              document.documentElement.lang = data.language;
            }

            if (typeof data.is_private === 'boolean') {
              updates.isPrivate = data.is_private;
            }

            if (data.username) {
              updates.username = data.username;
            }
            
            if (Object.keys(updates).length) {
              setProfileData(prev => ({ ...prev, ...updates }));
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        } finally {
          if (!isCancelled) {
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    };
    
    fetchUserProfile();

    return () => {
      isCancelled = true;
    };
  }, [user?.id]);

  return { 
    ...profileData, 
    isLoading, 
    setLanguage
  };
};
